/**
 * CustomerDetailPage.tsx — Admin customer composite detail view
 *
 * Purpose:
 *   Renders a complete customer overview from a single GET
 *   /admin/customers/:id/overview API call. Displays four sections:
 *   customer profile, active subscription, credit balance breakdown by type,
 *   and last 5 invoices.
 *
 * Public API:
 *   - CustomerDetailPage   Route-level page component
 *
 * Architecture Context:
 *   Per AT-50.02 directive, all four data sections come from one endpoint;
 *   no per-section API calls are made. Uses useCustomerOverview from
 *   useAdminApi50. Route param :id is read via useParams.
 *
 * @module packages/billing-admin/src/pages/CustomerDetailPage
 * @since 2026-06-01 (AT-50.02 — Plan & Customer Management Screens)
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import SlidePanel from '../components/SlidePanel';
import { useCustomerOverview } from '../hooks/useAdminApi50';
import type { CreditEntry, InvoiceSummary, SubscriptionSummary } from '../hooks/useAdminApi50';
import CreditGrantForm from '../components/customer/CreditGrantForm';
import CustomerForm from '../components/customer/CustomerForm';
import ConfirmDialog from '../components/ConfirmDialog';
import { useDeleteAdminCustomer, useUpdateAdminCustomer } from '../hooks/useAdminApi';

/**
 * StatusBadge — coloured pill for subscription/invoice status.
 *
 * @param status - Status string to display
 * @since 2026-06-01 (AT-50.02)
 */
function StatusBadge({ status }: { status: string }): React.JSX.Element {
  const colours: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    trialing: 'bg-blue-100 text-blue-800',
    past_due: 'bg-yellow-100 text-yellow-800',
    cancelled: 'bg-gray-100 text-gray-600',
    paid: 'bg-green-100 text-green-800',
    open: 'bg-yellow-100 text-yellow-800',
    draft: 'bg-gray-100 text-gray-500',
    void: 'bg-red-100 text-red-700',
    uncollectible: 'bg-red-100 text-red-700',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colours[status] ?? 'bg-gray-100 text-gray-600'}`}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

/**
 * ActiveSubscriptionCard — customer subscription summary card.
 *
 * @param subscription - Subscription data or null
 * @since 2026-06-01 (AT-50.02)
 */
function ActiveSubscriptionCard({ subscription }: { subscription: SubscriptionSummary | null }): React.JSX.Element {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <h2 className="mb-2 text-lg font-medium text-gray-900">Active Subscription</h2>
      {subscription ? (
        <div className="flex items-center justify-between">
          <span className="text-gray-700">{subscription.plan_name}</span>
          <StatusBadge status={subscription.status} />
        </div>
      ) : (
        <p className="text-sm text-gray-500">No active subscription.</p>
      )}
    </div>
  );
}

/**
 * CreditBalanceTable — credit balance breakdown by type.
 *
 * @param credits - Array of credit entries
 * @since 2026-06-01 (AT-50.02)
 */
function CreditBalanceTable({ credits }: { credits: CreditEntry[] }): React.JSX.Element {
  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 px-4 py-3">
        <h2 className="text-lg font-medium text-gray-900">Credit Balance</h2>
      </div>
      {credits.length === 0 ? (
        <p className="px-4 py-4 text-sm text-gray-500">No credits on file.</p>
      ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Type</th>
              <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Balance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {credits.map((c) => (
              <tr key={c.credit_type}>
                <td className="px-4 py-2 text-sm capitalize text-gray-700">{c.credit_type.replace('_', ' ')}</td>
                <td className="px-4 py-2 text-right text-sm text-gray-700">{c.currency} {c.balance.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * InvoiceListTable — last 5 invoices for the customer.
 *
 * @param invoices - Array of invoice summary objects
 * @since 2026-06-01 (AT-50.02)
 */
function InvoiceListTable({ invoices }: { invoices: InvoiceSummary[] }): React.JSX.Element {
  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 px-4 py-3">
        <h2 className="text-lg font-medium text-gray-900">Recent Invoices</h2>
      </div>
      {invoices.length === 0 ? (
        <p className="px-4 py-4 text-sm text-gray-500">No invoices found.</p>
      ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Invoice ID</th>
              <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Amount</th>
              <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Status</th>
              <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {invoices.map((inv) => (
              <tr key={inv.id}>
                <td className="px-4 py-2 font-mono text-xs text-gray-600">{inv.id}</td>
                <td className="px-4 py-2 text-right text-sm text-gray-700">{inv.currency} {inv.amount.toFixed(2)}</td>
                <td className="px-4 py-2 text-right"><StatusBadge status={inv.status} /></td>
                <td className="px-4 py-2 text-right text-sm text-gray-500">{new Date(inv.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * CustomerDetailPage — composite customer overview page.
 *
 * Loads all four data sections from a single API call and renders them
 * as stacked cards. Opens CreditGrantForm modal on 'Grant Credit' click.
 *
 * @returns AdminLayout-wrapped page element
 * @since 2026-06-01 (AT-50.02)
 */
export default function CustomerDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const customerId = id ?? '';
  const { data, isLoading, error, refetch } = useCustomerOverview(customerId);
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const updateCustomer = useUpdateAdminCustomer();
  const deleteCustomer = useDeleteAdminCustomer();

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center p-12">
          <span className="text-gray-500">Loading customer…</span>
        </div>
      </AdminLayout>
    );
  }

  if (error || !data) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
            {error?.message ?? 'Customer not found.'}
          </div>
          <button onClick={() => navigate('/customers')} className="mt-4 text-sm text-indigo-600 hover:underline">
            ← Back to Customers
          </button>
        </div>
      </AdminLayout>
    );
  }

  const { profile, subscription, credits, invoices } = data;

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        <button onClick={() => navigate('/customers')} className="text-sm text-indigo-600 hover:underline">
          ← Back to Customers
        </button>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">{profile.name}</h1>
              <p className="mt-1 text-sm text-gray-500">{profile.email}</p>
              <p className="mt-1 text-xs text-gray-400">Customer since {new Date(profile.created_at).toLocaleDateString()}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setEditOpen(true)}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Edit
              </button>
              <button
                onClick={() => setGrantModalOpen(true)}
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Grant Credit
              </button>
              <button
                onClick={() => setDeleteOpen(true)}
                className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
        <ActiveSubscriptionCard subscription={subscription} />
        <CreditBalanceTable credits={credits} />
        <InvoiceListTable invoices={invoices} />
      </div>
      <SlidePanel open={grantModalOpen} onClose={() => setGrantModalOpen(false)} size="sm" title="Grant Credits" category="Credit Management">
        <div className="px-6 py-6">
          <CreditGrantForm
            customerId={customerId}
            onSuccess={() => { setGrantModalOpen(false); void refetch(); }}
            onCancel={() => setGrantModalOpen(false)}
          />
        </div>
      </SlidePanel>
      <SlidePanel open={editOpen} onClose={() => setEditOpen(false)} size="md" title={`Edit: ${profile.name}`} category="Customer">
        <div className="px-6 py-6">
          <CustomerForm
            customer={{ id: profile.id, name: profile.name, email: profile.email }}
            onSubmit={async (input) => {
              await updateCustomer.mutateAsync({ customerId: profile.id, input });
              setEditOpen(false);
              await refetch();
            }}
            onCancel={() => setEditOpen(false)}
          />
        </div>
      </SlidePanel>
      <ConfirmDialog
        open={deleteOpen}
        title="Delete Customer"
        message={`Delete ${profile.name}? This removes the billing account record.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={() => {
          void deleteCustomer.mutateAsync(profile.id).then(() => {
            setDeleteOpen(false);
            navigate('/customers');
          });
        }}
        onCancel={() => setDeleteOpen(false)}
        loading={deleteCustomer.isPending}
      />
    </AdminLayout>
  );
}
